package one.healthium.insights.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyItemScope
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.shape.ZeroCornerSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEachIndexed
import kotlinx.coroutines.launch
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.ContentEntry
import one.healthium.insights.ui.components.items.ShoppingListItem
import one.healthium.insights.ui.utils.resolve
import one.healthium.ui.common.utils.toPaddingValuesHorizontal
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white

@Composable
fun ContentRenderer(
    modifier: Modifier = Modifier,
    content: List<ContentEntry>,
    onActionClicked: (ActionModel) -> Unit,
    isMetrics: Boolean,
    actionButton: String? = null,
    onTextActionClicked: () -> Unit = {},
    contentPadding: PaddingValues = PaddingValues(0.dp),
    persisScrollPosition: Boolean = false,
    persisKey: String? = null,
    headerContent: @Composable LazyItemScope.() -> Unit = {},
    footerContent: @Composable LazyItemScope.() -> Unit = {},
) {
    val state = persistableLazyListState(
        enabled = persisScrollPosition,
        key = persisKey,
    )
    val anchorState = rememberAnchorState()
    val scope = rememberCoroutineScope()

    val onActionClicked: Function1<ActionModel, Unit> = remember {
        { action: ActionModel ->
            if (action is ActionModel.Anchor) {
                scope.launch {
                    anchorState.getOrCreateBringIntoViewRequester(action.id).bringIntoView()
                }
            } else {
                onActionClicked(action)
            }
        }
    }

    LazyColumn(
        modifier = modifier.testTag("content_renderer"),
        state = state,
        contentPadding = contentPadding,
    ) {
        item(content = headerContent)

        content.fastForEachIndexed { index, entry ->
            when (entry) {
                is ContentEntry.StickyContent -> {
                    stickyHeader(contentType = entry) {
                        ContentEntryItem(
                            entry = entry.content,
                            anchorState = anchorState,
                            onActionClicked = onActionClicked,
                            actionButton = actionButton,
                            onTextActionClicked = onTextActionClicked,
                            isMetrics = isMetrics,
                        )
                    }
                }

                is ContentEntry.ShoppingList -> stickyHeader(contentType = entry) {
                    Box(
                        modifier = Modifier
                            .background(color = AppTheme.colors.windowBackground)
                            .padding(vertical = 6.dp)
                    ) {
                        ShoppingListItem(entry, onActionClicked)
                    }
                }

                is ContentEntry.GroupEntry -> entry.content.fastForEachIndexed { index, childEntry ->
                    item(contentType = childEntry) {
                        if (index == 0) {
                            Spacer(Modifier.height(12.dp))
                        }

                        val cornerRadius = entry.cornerRadius?.dp ?: AppTheme.shapes.contentGroupCornerSize
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 12.dp)
                                .background(
                                    color = entry.backgroundColor?.resolve() ?: white,
                                    shape = when (index) {
                                        0 -> RoundedCornerShape(topStart = cornerRadius, topEnd = cornerRadius)
                                        entry.content.size - 1 -> RoundedCornerShape(
                                            bottomStart = cornerRadius,
                                            bottomEnd = cornerRadius
                                        )

                                        else -> RoundedCornerShape(ZeroCornerSize)
                                    },
                                )
                                .padding(entry.insets.toPaddingValuesHorizontal())
                                .padding(
                                    if (entry.insets != null) {
                                        when (index) {
                                            0 -> PaddingValues(top = entry.insets.top.dp)
                                            entry.content.size - 1 -> PaddingValues(bottom = entry.insets.bottom.dp)
                                            else -> PaddingValues.Zero
                                        }
                                    } else PaddingValues.Zero
                                )
                        ) {
                            ContentEntryItem(
                                entry = childEntry,
                                anchorState = anchorState,
                                onActionClicked = onActionClicked,
                                actionButton = actionButton,
                                onTextActionClicked = onTextActionClicked,
                                isMetrics = isMetrics,
                            )
                        }
                    }
                }

                else -> item(contentType = entry) {
                    ContentEntryItem(
                        entry = entry,
                        anchorState = anchorState,
                        onActionClicked = onActionClicked,
                        actionButton = actionButton,
                        onTextActionClicked = onTextActionClicked,
                        isMetrics = isMetrics,
                    )
//                    Spacer(Modifier.height(6.dp))
                }
            }
        }

        item(content = footerContent)
    }
}